import React, { useState, useRef, useEffect } from 'react';
import { countries, Country, defaultCountry, getCountryByDDI } from '../../../utils/countries';
import ReactCountryFlag from "react-country-flag";

interface CountrySelectProps {
  value?: string;
  onChange: (country: Country) => void;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  isAdm?: boolean;
  theme?: string;
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  value = defaultCountry.ddi,
  onChange,
  disabled = false,
  style = {},
  isAdm = false,
  theme = 'light'
}) => {
  const countryValue = getCountryByDDI(value || '') || defaultCountry;

  const colorsTheme = {
    borderColors: { light: '#ddd', dark: '#555' },
    background: { light: '#fff', dark: '#242322' },
    disabledItems: { light: '#f5f5f5', dark: '#666' },
    primary: { light: '#333', dark: '#8A8A8A',  },
    inactiveItens: { light: '#999', dark: '#999' },
    hover: { light: '#f0f0f0', dark: '#a5a5a545' },
    label: { light: '#999', dark: '#777777da' }
  };
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.ddi.includes(searchTerm)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCountrySelect = (country: Country) => {
    onChange(country);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', maxWidth: !isAdm ? '94px' : '100px', overflow: 'visible', ...style }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          marginTop: !isAdm ? '1px' : '0px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: !isAdm ? '12px' : '5px',
          border: `1px solid ${!isAdm ? 'rgb(68 68 68)' : theme === 'light' ? 'rgb(204, 204, 204)' : 'rgb(68 68 68)'}`,
          borderRadius: '5px',
          backgroundColor: disabled ? colorsTheme.disabledItems[theme as 'light' | 'dark'] : colorsTheme.background[theme as 'light' | 'dark'],
          cursor: disabled ? 'not-allowed' : 'pointer',
          maxWidth: !isAdm ? '94px' : '100px',
          opacity: disabled ? 0.6 : 1,
          height: !isAdm ? '58px' : '39px',
        }}
      >
        {!isAdm && (
          <label style={{ marginTop: '-5px', fontSize: '12px', color: colorsTheme.label[theme as 'light' | 'dark'], fontWeight: '500' }}>País</label>
        )}

        <div>
          <ReactCountryFlag countryCode={countryValue.code} svg style={{ fontSize: "20px" }} />
          <span style={{
            fontSize: '14px',
            color: colorsTheme.primary[theme as 'light' | 'dark'],
            fontWeight: '500',
            marginTop: !isAdm ? '-1px' : '0px',
            marginLeft: '8px'
          }}>
            {countryValue.ddi}
          </span>
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '280px',
            right: 0,
            backgroundColor: colorsTheme.background[theme as 'light' | 'dark'],
            border: `1px solid ${colorsTheme.borderColors[theme as 'light' | 'dark']}`,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            maxHeight: '300px',
            overflow: 'hidden',
            marginTop: '4px',
          }}
        >
          <div style={{ padding: '8px' }}>
            <input
              type="text"
              placeholder="Buscar país..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${colorsTheme.borderColors[theme as 'light' | 'dark']}`,
                borderRadius: '6px',
                backgroundColor: colorsTheme.background[theme as 'light' | 'dark'],
                color: colorsTheme.primary[theme as 'light' | 'dark'],
                fontSize: '14px',
                outline: 'none',
              }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {filteredCountries.map((country) => (
              <div
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  cursor: 'pointer',
                  backgroundColor: countryValue.code === country.code ? colorsTheme.hover[theme as 'light' | 'dark'] : 'transparent',
                  borderBottom: `1px solid ${colorsTheme.borderColors[theme as 'light' | 'dark']}`,
                }}
                onMouseEnter={(e) => {
                  if (countryValue.code !== country.code) {
                    e.currentTarget.style.backgroundColor = colorsTheme.hover[theme as 'light' | 'dark'];
                  }
                }}
                onMouseLeave={(e) => {
                  if (countryValue.code !== country.code) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <ReactCountryFlag countryCode={country.code} svg style={{ fontSize: "24px" }} />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: colorsTheme.primary[theme as 'light' | 'dark']
                  }}>
                    {country.name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: colorsTheme.inactiveItens[theme as 'light' | 'dark']
                  }}>
                    {country.ddi}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
